import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowRight, User } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Link, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import type { BlogPost } from "@shared/blog-schema";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

const PROD_BASE = "https://saasgama.com";

function getCanonicalUrl(path: string): string {
  if (typeof window === "undefined") return `${PROD_BASE}${path}`;
  const { protocol, host } = window.location;
  // On production domains always use the canonical non-www HTTPS version
  if (host === "saasgama.com" || host === "www.saasgama.com") {
    return `${PROD_BASE}${path}`;
  }
  // Dev / localhost: self-referential so SEO tools show green
  return `${protocol}//${host}${path}`;
}

export default function BlogPage() {
  const [, navigate] = useLocation();
  const canonicalUrl = getCanonicalUrl("/blog");

  const { data: blogPosts = [], isLoading } = useQuery<BlogPost[]>({
    queryKey: ['/api/blog/posts'],
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>SaaSGama Blog | SaaS Management Insights & Trends</title>
        <meta name="description" content="Explore SaaSGama blog for SaaS management tips, cost optimization strategies, software insights, industry trends, and expert guidance now." />
        <meta name="keywords" content="SaaS Management, Cost Optimization, Software Insights, Industry Trends, Expert Guidance" />
        {/* Canonical: self-referential on dev, production URL on prod */}
        <link rel="canonical" href={canonicalUrl} />
        {/* Alternate: tell crawlers www and non-www are the same page (prod only) */}
        {canonicalUrl.startsWith(PROD_BASE) && (
          <link rel="alternate" href="https://www.saasgama.com/blog" hrefLang="en" />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/blog" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="SaaSGama Blog | SaaS Management Insights & Trends" />
        <meta property="og:description" content="Explore SaaSGama blog for SaaS management tips, cost optimization strategies, software insights, industry trends, and expert guidance now." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@SaaSGama" />
        <meta name="twitter:title" content="SaaSGama Blog | SaaS Management Insights & Trends" />
        <meta name="twitter:description" content="Explore SaaSGama blog for SaaS management tips, cost optimization strategies, software insights, industry trends, and expert guidance now." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebSite", "@id": "https://saasgama.com/#website", "url": "https://saasgama.com", "name": "SaaSGama", "publisher": { "@type": "Organization", "name": "SaaSGama", "url": "https://saasgama.com" } },
            { "@type": "Blog", "@id": "https://saasgama.com/blog/#blog", "url": "https://saasgama.com/blog", "name": "SaaSGama Blog", "description": "Insights, tips, and the latest updates on SaaS management, cost optimization, license management, and enterprise software trends.", "publisher": { "@type": "Organization", "name": "SaaSGama", "url": "https://saasgama.com" } },
            { "@type": "CollectionPage", "@id": "https://saasgama.com/blog/#collectionpage", "url": "https://saasgama.com/blog", "name": "SaaSGama Blog", "description": "Browse articles about SaaS management, software cost optimization, license tracking, and enterprise software governance." },
            { "@type": "WebPage", "@id": "https://saasgama.com/blog/#webpage", "url": "https://saasgama.com/blog", "name": "SaaSGama Blog", "isPartOf": { "@id": "https://saasgama.com/#website" } },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/blog/#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://saasgama.com/blog" }
            ]}
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              SaaS Management <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Insights, tips, and the latest updates on SaaS management, cost optimization, and enterprise software trends.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Card key={post.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
                  {post.featuredImage && (
                    <div className="overflow-hidden rounded-t-lg">
                      <img 
                        src={post.featuredImage} 
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate((post.publishedAt || post.createdAt).toString())}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        5 min read
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-3">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-500">
                        <User className="w-4 h-4 mr-1" />
                        {post.authorName}
                      </div>
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex gap-1">
                          {post.tags.slice(0, 2).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      className="w-full mt-4 group-hover:bg-blue-50 group-hover:text-blue-600"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
